import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Form, Button, ProgressBar } from "react-bootstrap";

const StyledFormField = ({ label, name, value, onChange, placeholder, type = 'text', as = 'input', rows, maxLength, required, children }) => (
    <Form.Group className="mb-2">
        <Form.Label className="fw-semibold small text-primary">
            {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Control
            as={as}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
            maxLength={maxLength}
            required={required}
            size="sm"
            className="rounded-3 shadow-none border-primary-subtle"
            style={{ fontSize: 12, resize: as === 'textarea' ? 'none' : 'vertical' }}
        >
            {children}
        </Form.Control>
    </Form.Group>
);

const StatusToggle = ({ name, checked, onChange, label = 'Active' }) => (
    <div className="form-check mb-3">
        <Form.Check
            type="checkbox"
            name={name}
            id={name}
            label={label}
            checked={checked === 1}
            onChange={onChange}
            className="shadow-none"
            style={{ fontSize: 12 }}
        />
    </div>
);

const FileUploadField = ({ label, name, files, onFilesChange, onRemoveFile, uploading, uploadProgress }) => (
    <StyledFormField label={label} as="div">
        <div className="d-flex flex-column align-items-center justify-content-center gap-2 p-2 border border-dashed border-primary-subtle rounded-3 bg-light"
            style={{ minHeight: 80, cursor: 'pointer' }}
            onClick={() => document.getElementById(`${name}-input`).click()}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const droppedFiles = Array.from(e.dataTransfer.files);
                onFilesChange(droppedFiles);
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}>
            {uploading ? (
                <div className="d-flex flex-column align-items-center gap-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                    <small>Uploading...</small>
                    {uploadProgress > 0 && (
                        <ProgressBar now={uploadProgress} className="w-100" style={{ height: 6 }} />
                    )}
                </div>
            ) : files.length === 0 ? (
                <div className="text-center text-muted small">
                    <div>📎 Drop files here or click to browse</div>
                    <div className="mt-1">Supports PDF, images, docs (max 10MB)</div>
                </div>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {files.map((file, index) => (
                        <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-white rounded-2 border">
                            <div className="d-flex align-items-center gap-2 flex-fill">
                                <div className="text-muted" style={{ fontSize: 20 }}>
                                    {file.type.startsWith('image/') ? '🖼️' :
                                        file.name.toLowerCase().endsWith('.pdf') ? '📄' : '📎'}
                                </div>
                                <div className="small flex-fill">
                                    <div className="fw-semibold">{file.name}</div>
                                    <div className="text-muted">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                </div>
                            </div>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                className="rounded-circle p-1"
                                style={{ width: 24, height: 24, fontSize: 10 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveFile(index);
                                }}
                            >
                                ×
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
        <input
            id={`${name}-input`}
            type="file"
            multiple
            accept=".pdf,image/*,.doc,.docx"
            className="d-none"
            onChange={(e) => onFilesChange(Array.from(e.target.files))}
        />
    </StyledFormField>
);

const AddCategoryForm = ({ isOpen, onToggle, onRefresh }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        is_active: 1
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        setTimeout(async () => {
            try {
                await axios.post('https://www.azzenterprises.com/api/categories/create', formData);
                setFormData({ name: '', description: '', is_active: 1 });
                onToggle();
                onRefresh?.();
            } catch (error) {
                console.error('Error adding category:', error);
            } finally {
                setLoading(false);
            }
        }, 1000);
    };

    const handleCancel = () => {
        setFormData({ name: '', description: '', is_active: 1 });
        setLoading(false);
        onToggle();
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0, x: 25, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 25, scale: 0.95 }}
            transition={{
                duration: 0.15,
                ease: 'easeOut',
                type: 'spring',
                stiffness: 400,
                damping: 30
            }}
            className="me-3 px-3 py-3 bg-white border border-primary-subtle shadow-lg rounded-3 small position-absolute"
            style={{
                bottom: 0,
                right: 150,
                minWidth: 300,
                maxWidth: 350,
                fontSize: 13,
                fontWeight: 500,
                backdropFilter: 'blur(20px)',
                whiteSpace: 'normal',
                zIndex: 10
            }}
        >
            <div
                className="position-absolute top-0 end-0 me-2 mt-1 z-3 p-1"
                style={{
                    fontSize: 16,
                    cursor: 'pointer',
                    color: '#667eea',
                    fontWeight: 600,
                    borderRadius: '50%',
                    transition: 'all 0.2s ease'
                }}
                onClick={handleCancel}
                onMouseEnter={(e) => e.target.style.background = 'rgba(102,126,234,0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
                ×
            </div>

            <Form onSubmit={handleSubmit} className="d-flex flex-column gap-2 pt-3">
                <StyledFormField
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Category name"
                    maxLength={100}
                    required
                />

                <StyledFormField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Description (optional)"
                    as="textarea"
                    rows={2}
                />

                <StatusToggle
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                />

                <div className="d-flex gap-2">
                    <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={loading}
                        className="flex-fill fw-semibold rounded-3 shadow-none px-3"
                        style={{
                            fontSize: 12,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            padding: '6px 12px'
                        }}
                    >
                        {loading ? (
                            <span className="d-flex align-items-center gap-2">
                                <div className="spinner-border spinner-border-sm" role="status" style={{ width: 16, height: 16 }}></div>
                                <span>Adding...</span>
                            </span>
                        ) : 'Add Category'}
                    </Button>
                    <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        disabled={loading}
                        className="fw-semibold rounded-3 shadow-none px-3"
                        style={{
                            fontSize: 12,
                            padding: '6px 12px',
                            borderColor: 'rgba(102, 126, 234, 0.5)'
                        }}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </div>
            </Form>
        </motion.div>
    );
};

const AddTemplateForm = ({ isOpen, onToggle, onRefresh }) => {
    const [formData, setFormData] = useState({
        template_name: '',
        subject: '',
        body_text: '',
        attachments: '[]',
        status: 1
    });

    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleFilesChange = (newFiles) => {
        setFiles(prev => {
            const existingNames = new Set(prev.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
            return [...prev.slice(0, 4), ...uniqueNewFiles].slice(0, 5);
        });
    };

    const handleRemoveFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const uploadFilesToS3 = useCallback(async () => {
        if (files.length === 0) return '[]';

        setUploading(true);
        setUploadProgress(0);

        try {
            const uploadPromises = files.map(async (file) => {
                const presignResponse = await axios.post('https://www.azzenterprises.com/api/upload/presign', {
                    filename: file.name,
                    filetype: file.type,
                    size: file.size
                });

                const { url: presignedUrl, key } = presignResponse.data;

                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    body: file,
                    headers: {
                        'Content-Type': file.type
                    }
                });

                if (!uploadResponse.ok) throw new Error('S3 upload failed');

                return {
                    filename: file.name,
                    type: file.type,
                    size: file.size,
                    url: `https://${key}`
                };
            });

            const attachments = await Promise.all(uploadPromises);
            setUploadProgress(100);

            return JSON.stringify(attachments);
        } catch (error) {
            console.error('Upload failed:', error);
            throw error;
        } finally {
            setTimeout(() => {
                setUploading(false);
                setUploadProgress(0);
            }, 1000);
        }
    }, [files]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        setTimeout(async () => {
            try {
                const attachmentsJson = await uploadFilesToS3();

                const submitData = {
                    ...formData,
                    attachments: attachmentsJson
                };

                await axios.post('https://www.azzenterprises.com/api/templates/create', submitData);

                setFormData({
                    template_name: '',
                    subject: '',
                    body_text: '',
                    attachments: '[]',
                    status: 1
                });
                setFiles([]);
                onToggle();
                onRefresh?.();
            } catch (error) {
                console.error('Error adding template:', error);
            } finally {
                setLoading(false);
            }
        }, 1000);
    };

    const handleCancel = () => {
        setFormData({
            template_name: '',
            subject: '',
            body_text: '',
            attachments: '[]',
            status: 1
        });
        setFiles([]);
        setLoading(false);
        onToggle();
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0, x: 25, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 25, scale: 0.95 }}
            transition={{
                duration: 0.15,
                ease: 'easeOut',
                type: 'spring',
                stiffness: 400,
                damping: 30
            }}
            className="me-3 px-3 py-3 bg-white border border-primary-subtle shadow-lg rounded-3 small position-absolute"
            style={{
                bottom: 0,
                right: 150,
                minWidth: 420,
                maxWidth: 480,
                fontSize: 13,
                fontWeight: 500,
                backdropFilter: 'blur(20px)',
                whiteSpace: 'normal',
                zIndex: 10,
                maxHeight: '85vh',
                overflowY: 'auto'
            }}
        >
            <div
                className="position-absolute top-0 end-0 me-2 mt-1 z-3 p-1"
                style={{
                    fontSize: 16,
                    cursor: 'pointer',
                    color: '#f5576c',
                    fontWeight: 600,
                    borderRadius: '50%',
                    transition: 'all 0.2s ease'
                }}
                onClick={handleCancel}
                onMouseEnter={(e) => e.target.style.background = 'rgba(245,87,108,0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
                ×
            </div>

            <Form onSubmit={handleSubmit} className="d-flex flex-column gap-2 pt-3">
                <StyledFormField
                    label="Template Name"
                    name="template_name"
                    value={formData.template_name}
                    onChange={handleChange}
                    placeholder="Template name"
                    maxLength={150}
                    required
                />

                <StyledFormField
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject line"
                    maxLength={255}
                />

                <StyledFormField
                    label="Body Text"
                    name="body_text"
                    value={formData.body_text}
                    onChange={handleChange}
                    placeholder="Plain text content"
                    as="textarea"
                    rows={4}
                    required
                />

                <FileUploadField
                    label="Attachments"
                    name="attachments"
                    files={files}
                    onFilesChange={handleFilesChange}
                    onRemoveFile={handleRemoveFile}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                />

                <StatusToggle
                    name="status"
                    checked={formData.status}
                    onChange={handleChange}
                    label="Active"
                />

                <div className="d-flex gap-2">
                    <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={loading || uploading}
                        className="flex-fill fw-semibold rounded-3 shadow-none px-3"
                        style={{
                            fontSize: 12,
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            border: 'none',
                            padding: '6px 12px'
                        }}
                    >
                        {loading ? (
                            <span className="d-flex align-items-center gap-2">
                                <div className="spinner-border spinner-border-sm" role="status" style={{ width: 16, height: 16 }}></div>
                                <span>Creating...</span>
                            </span>
                        ) : uploading ? 'Uploading...' : 'Add Template'}
                    </Button>
                    <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        disabled={loading || uploading}
                        className="fw-semibold rounded-3 shadow-none px-3"
                        style={{
                            fontSize: 12,
                            padding: '6px 12px',
                            borderColor: 'rgba(245, 87, 108, 0.5)'
                        }}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </div>
            </Form>
        </motion.div>
    );
};

const actions = [
    {
        label: 'Add Category',
        hint: 'Create and manage notification categories to keep templates organised',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        form: 'category'
    },
    {
        label: 'Add Template',
        hint: 'Create customisable templates within a category for structured and efficient notifications',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        form: 'template'
    }
];

const FabMenu = ({ onRefresh }) => {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(null);
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [showTemplateForm, setShowTemplateForm] = useState(false);

    const toggleCategoryForm = (e) => {
        if (e) e.stopPropagation();
        setShowTemplateForm(false);
        setShowCategoryForm(!showCategoryForm);
        setHovered(showCategoryForm ? null : 0);
    };

    const toggleTemplateForm = (e) => {
        if (e) e.stopPropagation();
        setShowCategoryForm(false);
        setShowTemplateForm(!showTemplateForm);
        setHovered(showTemplateForm ? null : 1);
    };

    const handleMainFABClick = () => {
        setShowCategoryForm(false);
        setShowTemplateForm(false);
        setTimeout(() => {
            setOpen(!open);
        }, 150);
    };

    return (
        <div className="position-fixed bottom-0 end-0 mb-4 z-3 d-flex flex-column align-items-end" style={{ marginRight: 40 }}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={{
                            visible: { transition: { staggerChildren: 0.1 } }
                        }}
                        style={{
                            position: 'absolute',
                            bottom: '60px',
                            right: 30,
                        }}
                    >
                        {actions.map((item, i) => (
                            <motion.div
                                key={i}
                                onMouseEnter={() => setHovered(i)}
                                onMouseLeave={() => setHovered(null)}
                                className="position-relative d-flex align-items-center justify-content-end mb-3"
                            >
                                <AnimatePresence>
                                    {item.form === 'category' && showCategoryForm ? (
                                        <AddCategoryForm
                                            isOpen={true}
                                            onToggle={toggleCategoryForm}
                                            onRefresh={onRefresh}
                                        />
                                    ) : item.form === 'template' && showTemplateForm ? (
                                        <AddTemplateForm
                                            isOpen={true}
                                            onToggle={toggleTemplateForm}
                                            onRefresh={onRefresh}
                                        />
                                    ) : hovered === i ? (
                                        <motion.div
                                            initial={{ opacity: 0, x: 25, scale: 0.95 }}
                                            animate={{ opacity: 1, x: 0, scale: 1 }}
                                            exit={{ opacity: 0, x: 25, scale: 0.95 }}
                                            transition={{
                                                duration: 0.15,
                                                ease: 'easeOut',
                                                type: 'spring',
                                                stiffness: 400,
                                                damping: 30
                                            }}
                                            className="me-3 px-3 py-2 bg-white border border-light shadow-lg rounded-3 small position-relative"
                                            style={{
                                                whiteSpace: 'nowrap',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                backdropFilter: 'blur(10px)',
                                                cursor: 'pointer'
                                            }}
                                            onClick={item.form === 'category' ? toggleCategoryForm : toggleTemplateForm}
                                        >
                                            {item.hint}
                                            <span
                                                className="ms-2"
                                                style={{
                                                    fontSize: 16,
                                                    color: item.form === 'category' ? '#667eea' : '#f5576c'
                                                }}
                                            >
                                                {(item.form === 'category' && showCategoryForm) ||
                                                    (item.form === 'template' && showTemplateForm) ? '✕' : '›'}
                                            </span>
                                        </motion.div>
                                    ) : null}
                                </AnimatePresence>

                                <motion.button
                                    variants={{
                                        hidden: { opacity: 0, y: 20, scale: 0.95 },
                                        visible: { opacity: 1, y: 0, scale: 1 }
                                    }}
                                    transition={{ duration: 0.25, ease: 'easeOut' }}
                                    className="border-0 rounded shadow-lg px-3 py-2 fw-semibold text-white"
                                    style={{
                                        minWidth: 150,
                                        fontSize: 14,
                                        background: item.gradient,
                                        backdropFilter: 'blur(10px)'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (item.form === 'category') {
                                            toggleCategoryForm(e);
                                        } else {
                                            toggleTemplateForm(e);
                                        }
                                    }}
                                >
                                    {item.label}
                                </motion.button>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                onClick={handleMainFABClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-0 rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                style={{
                    width: 52,
                    height: 52,
                    background: '#fff',
                    fontSize: 24,
                    fontWeight: 600
                }}
            >
                <motion.span
                    animate={{ rotate: open ? 45 : 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ color: '#ff4d8d' }}
                >
                    +
                </motion.span>
            </motion.button>
        </div>
    );
};

export default FabMenu;
